import axios from 'axios';
import { navigate } from '@reach/router';
import { fireEventOnce } from '@beef/shop-common';

import {
  getCurrentFederalRegionName,
  getCurrentRegionName,
  getRequestVerificationToken,
} from 'store/common/settings/selectors';
import { getSearchQuery } from 'store/common/navigation/selectors';
import { addProductToBasket } from 'store/common/basket/actions';
import { DETAILS_PART_OF_PATH } from 'store/ProductDetails/constants';
import { convertLegacyProducts, convertLegacyPagingData } from 'pages/ProductCatalog/dataConverter';
import { simulateResize } from 'utils/resize';
import { removeDuplicates } from 'utils/non-duplicate';
import { setTakeawayOffices } from 'utils/set-takeaway-offices';
import pushEvent, {
  pushToDashamail,
  EVENT_CATALOG_PRODUCT_CLICK_WITH_BADGES,
  EVENT_CATALOG_LOAD,
  EVENT_COMMON_SWITCH_TO_CARD,
  EVENT_COMMON_ADD_TO_CART,
  EVENT_CATALOG_UPDATE,
  EVENT_CATALOG_VIEW_PROMO_BANNER,
  EVENT_CATALOG_CLICK_PROMO_BANNER,
  EVENT_ADD_TO_CART_WITH_TRADE_IN,
  EVENT_ADD_TO_CART_WITH_BNPL,
  EVENT_NESTED_COLLECTION_ITEM_VIEW,
  EVENT_NESTED_COLLECTION_ITEM_CLICK,
  EVENT_FILTER_SELECT,
  EVENT_FILTER_CLEAR,
  EVENT_CATALOG_ADD_PRODUCT_WITH_INSTALLMENT_TO_BASKET,
} from 'pushEvent';
import { FOLLOW_LINK, REQUEST_INPROGRESS, REQUEST_SUCCESS } from 'store/constants';
import { isTradeInActive } from 'store/widgets/TradeInCalculator/selectors';
import { SET_CATALOG_FILTERS_VALUES_CHANGED } from 'store/ProductCatalog/controls/constants';
import {
  FILTER_SLUG_PRICE,
  FORCE_REMAINS_ONLY,
  SET_FILTER_VALUES,
} from 'store/ProductCatalog/filters/constants';

import {
  getCatalogProductsList,
  getCatalogProductsUpdateMethodUrl,
  getCatalogProductPromotionId,
  getProductIsTariffDict,
  getProductDpcIdDict,
  getProductInBasketDict,
  getCatalogProductsRequestStatus,
  getCatalogProductOffices,
  getCatalogProductOfficesLength,
  getCatalogOfficesRequestStatus,
  getCatalogCurrentPageSkipComposer,
  getCatalogCurrentPage,
  getCatalogProductTitle,
  getCatalogProductsResponseData,
  getCatalogProductsReadyToSwitch,
  getProductSeoDataList,
  getProductFullSeoDataDict,
  getProductIsPreorderDict,
  selectIsBnplAvailableInProduct,
  selectIsInstallmentAvailableInProduct,
} from './products/selectors';
import { getOutOfStockSuccessText, getOutOfStockErrorText } from './outofstock/selectors';
import {
  getCatalogActiveFiltersData,
  getFilterRangeValueFrom,
  getFilterRangeValueTo,
  getCatalogFiltersPriceChanged,
  getCatalogFullUrlComposer,
  getAllFiltersActiveOptionsCount,
  getCatalogProductsTag,
  getAllFiltersSettings,
  getFilterRangeValueFromRaw,
  getFilterRangeValueToRaw,
  getFilterRangeUsed,
  selectAllFiltersForAnalytics,
} from './filters/selectors';
import {
  getCatalogActiveSortById,
  getCatalogFiltersBarChangedState,
  getCatalogForcedRemainsOnly,
} from './controls/selectors';
import {
  getCatalogSubCollectionId,
  getCatalogHeadline,
  getCatalogIsTariffCollectionFlag,
  getActiveFancyNumber,
  getTariffParameterIcons,
  getCatalogRemainsOnly,
} from './selectors';
import {
  CATALOG_PRODUCTS_READY_TO_SWITCH,
  REQUEST_CATALOG_PRODUCTS,
  REQUEST_CATALOG_PRODUCTS_ERROR,
  REQUEST_CATALOG_PRODUCTS_SUCCESS,
  REQUEST_CATALOG_PRODUCT_OFFICES,
  REQUEST_CATALOG_PRODUCT_OFFICES_ERROR,
  REQUEST_CATALOG_PRODUCT_OFFICES_SUCCESS,
  REQUEST_CATALOG_PRODUCTS_APPLY,
  REQUEST_CATALOG_PRODUCTS_APPLIED,
  CATALOG_UPDATE_ERROR_UNEXPECTED,
  CATALOG_UPDATE_ERROR_EMPTY,
  SUBSCRIBED_PRODUCT,
  ERROR_CLASSNAME,
  INFO_CLASSNAME,
  UP_EMOJI,
  STATUS_FAIL_EMOJI,
  REQUEST_CATALOG_SHOW_MORE_LOADING,
} from './constants';
import {
  SET_CATALOG_PRODUCT_OFFICES,
  SET_CATALOG_PRODUCT_HOVERED_ID,
  SET_CATALOG_PRODUCT_HOVERED_TITLE,
  UNSET_CATALOG_PRODUCT_OFFICES,
  REQUEST_AGREEMENT_CONTENT,
  REQUEST_AGREEMENT_CONTENT_SUCCESS,
  REQUEST_AGREEMENT_CONTENT_FAIL,
  REQUEST_PROPS_CONTENT,
  REQUEST_PROPS_CONTENT_SUCCESS,
  REQUEST_PROPS_CONTENT_FAIL,
  SET_INITIAL_CATALOG_DELIVERY_OFFICES,
} from './takeaway/constants';
import {
  getCatalogHoveredProductId,
  getCatalogDeliveryAgreementContent,
  getCatalogDeliveryAgreementUrl,
  getCatalogDeliveryPropsContent,
  getCatalogDeliveryPropsUrl,
  getCatalogProductOfficesListRaw,
  getCatalogTakeawaySettings,
} from './takeaway/selectors';
import { scrollAnimate } from '../../utils/scroll';
import { resetCatalogScroll } from '../../pages/ProductCatalog/helpers';
import { getBaseUrl } from '../ProductDetails/selectors';

/**
 * source is a global variable
 * used to abort product filter request
 *
 * while debounceTimer is a timer for delayed call of the same request
 */
let productRequestSource = null;
let productRequestDebounceTimer = null;

export const requestCatalogProductsError = (requestData, responseData) => ({
  type: REQUEST_CATALOG_PRODUCTS_ERROR,
  requestData,
  message: responseData.message,
  errorType: responseData.errorType,
});

/**
 * We only apply data when preloader finished fadingOut and we got success from response
 */
export const applyProductsResponseData = () => (dispatch, getState) => {
  let state = getState();
  const readyToSwitch = getCatalogProductsReadyToSwitch(state);
  const requestSuccess = getCatalogProductsRequestStatus(state) === REQUEST_SUCCESS;
  // if we after update have no active options => no clear button
  const filtersAreChanged = getCatalogFiltersBarChangedState(state);
  if (filtersAreChanged) {
    const activeOptionsCount = getAllFiltersActiveOptionsCount(state);
    if (activeOptionsCount === 0) {
      dispatch(setFiltersBarChange(false));
    }
  }
  // ===

  if (readyToSwitch && requestSuccess) {
    const { products, paging, seo, breadcrumbs, title } = getCatalogProductsResponseData(state);
    const previousTitle = getCatalogHeadline(state);
    dispatch({
      type: REQUEST_CATALOG_PRODUCTS_APPLY,
      list: products,
      paging,
      seo,
      breadcrumbs,
      title: title || previousTitle,
    });

    dispatch({
      type: REQUEST_CATALOG_PRODUCTS_APPLIED,
    });

    state = getState();
    const page = getCatalogCurrentPage(state);
    const href = getCatalogFullUrlComposer(state)(page);

    /** keep scroll from jumping */
    resetCatalogScroll();

    navigate(href);
    requestAnimationFrame(simulateResize);

    const regionName = getCurrentRegionName(state);
    const federalRegionName = getCurrentFederalRegionName(state);
    const impressions = getProductSeoDataList(state);
    pushEvent(EVENT_CATALOG_UPDATE, {
      regionName,
      federalRegionName,
      impressions,
    });
  }
};

export const applyProductsShowMore = () => (dispatch, getState) => {
  const state = getState();
  const { products, paging, seo, breadcrumbs, title } = getCatalogProductsResponseData(state);
  const productList = getCatalogProductsList(state);
  const previousTitle = getCatalogHeadline(state);

  dispatch({
    type: REQUEST_CATALOG_PRODUCTS_APPLY,
    list: [...productList, ...products],
    paging,
    seo,
    breadcrumbs,
    title: title || previousTitle,
  });

  dispatch({
    type: REQUEST_CATALOG_PRODUCTS_APPLIED,
  });

  requestAnimationFrame(simulateResize);
};

export const setCatalogProductOffices = (offices) => ({
  type: SET_CATALOG_PRODUCT_OFFICES,
  offices,
});

export const unsetCatalogProductOffices = () => ({
  type: UNSET_CATALOG_PRODUCT_OFFICES,
  offices: [],
});

export const setAllCatalogTakeawayOffices = () => (dispatch, getState) => {
  const state = getState();
  const officesList = getCatalogProductOfficesListRaw(state);
  const settings = getCatalogTakeawaySettings(state);
  const allOffices = setTakeawayOffices(officesList, settings);
  dispatch({
    type: SET_INITIAL_CATALOG_DELIVERY_OFFICES,
    allOffices,
  });
};

export const loadCatalogProducts = () => (dispatch, getState) => {
  const state = getState();

  const baseUrl = getBaseUrl(state);
  /* dashamail push */
  const subCollectionId = getCatalogSubCollectionId(state);
  pushToDashamail(EVENT_CATALOG_LOAD, { subCollectionId });
  // ===
  pushEvent(EVENT_CATALOG_LOAD, { baseUrl });

  const regionName = getCurrentRegionName(state);
  const federalRegionName = getCurrentFederalRegionName(state);
  const impressions = getProductSeoDataList(state);
  pushEvent(EVENT_CATALOG_UPDATE, {
    regionName,
    federalRegionName,
    impressions,
  });
};

export const switchToCard = (id) => (dispatch, getState) => {
  const state = getState();
  const currentProduct = getProductFullSeoDataDict(state)[id];
  currentProduct.dimension15 = (currentProduct || {}).dimension15 || null;

  pushEvent(EVENT_COMMON_SWITCH_TO_CARD, currentProduct);

  dispatch({
    type: FOLLOW_LINK,
    payload: window.location.pathname,
  });
};

export const onCatalogBuyButtonClick =
  (id, btn = '') =>
  (dispatch, getState) => {
    const state = getState();
    const currentProduct = getProductFullSeoDataDict(state)[id];
    const isPreorder = getProductIsPreorderDict(state)[id];
    const isTradeIn = isTradeInActive(state); // = undefined
    const hasBnpl = selectIsBnplAvailableInProduct(state)[id];
    const hasInstallment = selectIsInstallmentAvailableInProduct(state)[id];
    const baseUrl = getBaseUrl(state);
    const name = currentProduct?.name;

    if (isPreorder) {
      dispatch(switchToCard(id));
    } else {
      pushEvent(EVENT_COMMON_ADD_TO_CART, currentProduct);
      if (isTradeIn) {
        pushEvent(EVENT_ADD_TO_CART_WITH_TRADE_IN, {
          name: currentProduct?.name,
          price: currentProduct?.price,
          brand: currentProduct?.brand,
        });
      }
    }

    if (hasInstallment) {
      pushEvent(EVENT_CATALOG_ADD_PRODUCT_WITH_INSTALLMENT_TO_BASKET, {
        product_id: id,
        product_name: name,
        button_text: btn,
      });
    }

    if (hasBnpl) {
      pushEvent(EVENT_ADD_TO_CART_WITH_BNPL, {
        [baseUrl]: {
          id,
          name,
        },
      });
    }
  };

export const requestCatalogProductsSuccess =
  (requestData, { filters, ...payload }, isShowMore) =>
  (dispatch) => {
    dispatch({
      type: REQUEST_CATALOG_PRODUCTS_SUCCESS,
      requestData,
      payload,
      filters,
    });

    if (isShowMore) {
      dispatch(applyProductsShowMore());
    } else {
      dispatch(applyProductsResponseData());
    }
  };

export const setProductsRequestReadyToSwitch = () => (dispatch) => {
  dispatch({
    type: CATALOG_PRODUCTS_READY_TO_SWITCH,
  });

  dispatch(applyProductsResponseData());
};

export const triggerUpdateRequest = () => (dispatch, getState) => {
  const state = getState();
  const requestStatus = getCatalogProductsRequestStatus(state);

  /**
   * Request might be fired earlier by `updateCatalogProductsDelicately`
   * normally by `updateCatalogProducts` or subsequently due to cancel,
   * thus we must check if it is not already in progress before dispatching
   */
  if (requestStatus !== REQUEST_INPROGRESS) {
    dispatch({
      type: REQUEST_CATALOG_PRODUCTS,
    });
  }
};

/**
 * instantly update catalog products
 */
export const updateCatalogProducts =
  ({ page = null, shouldScrollUp, isShowMore } = {}) =>
  (dispatch, getState) => {
    const state = getState();

    /** if we switch in pagination or sort, we scroll up after  */
    shouldScrollUp = shouldScrollUp || page !== null;
    if (shouldScrollUp && !isShowMore) {
      scrollAnimate(document.getElementById('nestedCollection'));
    }

    page = page || 1;

    const PriceFrom = getFilterRangeValueFrom(state);
    const PriceTo = getFilterRangeValueTo(state);
    const isPriceUpdatedByUser = getCatalogFiltersPriceChanged(state);
    const Query = getSearchQuery(state);
    const tariffParameterIcons = getTariffParameterIcons(state);
    const remainsOnly = getCatalogForcedRemainsOnly(state) || getCatalogRemainsOnly(state);

    const data = {
      CollectionId: getCatalogSubCollectionId(state),
      Items: getCatalogActiveFiltersData(state),
      ...(isPriceUpdatedByUser ? { PriceFrom, PriceTo } : {}),
      SortType: getCatalogActiveSortById(state),
      Skip: getCatalogCurrentPageSkipComposer(state)(page),
      isPriceUpdatedByUser,
      ...(Query ? { Query } : {}),
      IsTariff: getCatalogIsTariffCollectionFlag(state),
      remainsOnly,
      Tag: getCatalogProductsTag(state)(page),
      // ViewType: 'List',
    };

    if (isShowMore) {
      dispatch({
        type: REQUEST_CATALOG_SHOW_MORE_LOADING,
        payload: true,
      });
    } else {
      dispatch(triggerUpdateRequest());
    }

    const token = getRequestVerificationToken(state);
    const url = getCatalogProductsUpdateMethodUrl(state);

    clearTimeout(productRequestDebounceTimer);
    if (productRequestSource !== null) productRequestSource.cancel('Cancelled by the user');

    productRequestSource = axios.CancelToken.source();
    axios({
      method: 'POST',
      url,
      data,
      cancelToken: productRequestSource.token,
      headers: {
        __RequestVerificationToken: token,
        'X-Requested-With': 'XMLHttpRequest',
      },
    })
      .then(({ data: responseData }) => {
        if (typeof responseData.filters === 'undefined') {
          throw new Error(`Incorrect data returned in call to '${url}'`);
        }
        dispatch(updateCatalogFilters(responseData.filters));
        if (!Array.isArray(responseData.products) && typeof responseData.products === 'object') {
          dispatch(
            requestCatalogProductsSuccess(
              data,
              {
                products: convertLegacyProducts(responseData.products.list, tariffParameterIcons),
                paging: responseData.products.paging,
                seo: responseData.seo,
                breadcrumbs: responseData.breadcrumbs,
                title: responseData.title,
              },
              isShowMore,
            ),
          );
          // TODO: remove following when products format changes to object
        } else if (responseData.products.length) {
          dispatch(
            requestCatalogProductsSuccess(
              data,
              {
                products: convertLegacyProducts(responseData.products, tariffParameterIcons),
                paging: convertLegacyPagingData(responseData.paging),
                seo: responseData.seo,
                breadcrumbs: responseData.breadcrumbs,
                title: responseData.title,
              },
              isShowMore,
            ),
          );
        } else {
          dispatch(
            requestCatalogProductsError(data, {
              errorType: CATALOG_UPDATE_ERROR_EMPTY,
            }),
          );
        }
        // set null to forceRemains
        if (getCatalogForcedRemainsOnly(state)) {
          dispatch(forceRemainsOnly());
        }
        dispatch({
          type: REQUEST_CATALOG_SHOW_MORE_LOADING,
          payload: false,
        });
      })
      .catch((err) => {
        if (!axios.isCancel(err)) {
          // do not throw error if request was cancelled manually
          dispatch(
            requestCatalogProductsError(data, { errorType: CATALOG_UPDATE_ERROR_UNEXPECTED }),
          );
          throw err;
        }
        dispatch({
          type: REQUEST_CATALOG_SHOW_MORE_LOADING,
          payload: false,
        });
      });
    return productRequestSource;
  };

export const showMoreProducts = () => (dispatch, getState) => {
  const state = getState();
  const current = getCatalogCurrentPage(state);

  dispatch(updateCatalogProducts({ page: current + 1, isShowMore: true }));
};

/**
 * Update catalog products after timeout
 */
export const updateCatalogProductsDelicately = () => (dispatch) => {
  dispatch(triggerUpdateRequest());

  if (productRequestSource !== null) productRequestSource.cancel('Cancelled by the user');
  clearTimeout(productRequestDebounceTimer);
  productRequestDebounceTimer = setTimeout(() => {
    productRequestDebounceTimer = null;
    dispatch(updateCatalogProducts());
  }, 800);
};

/**
 * Get delivery points of a product
 */
export const requestCatalogProductOfficesSuccess = (requestData, pointsRaw) => (dispatch) => {
  const points = removeDuplicates(JSON.parse(pointsRaw), 'address');
  dispatch({
    type: REQUEST_CATALOG_PRODUCT_OFFICES_SUCCESS,
    requestData,
    points,
  });
  dispatch(setCatalogProductOffices(points));
  dispatch(setAllCatalogTakeawayOffices());
};

export const requestCatalogProductOfficesError = (requestData, responseData) => (dispatch) => {
  dispatch({
    type: REQUEST_CATALOG_PRODUCT_OFFICES_ERROR,
    requestData,
    message: responseData.message,
  });

  dispatch(unsetCatalogProductOffices());
};

export const setCatalogHoveredProductId = (id) => ({
  type: SET_CATALOG_PRODUCT_HOVERED_ID,
  id,
});

export const setCatalogHoveredProductTitle = (title) => ({
  type: SET_CATALOG_PRODUCT_HOVERED_TITLE,
  title,
});

export const requestAgreementData = (agreementUrl) => (dispatch) => {
  dispatch({
    type: REQUEST_AGREEMENT_CONTENT,
  });
  axios
    .get(agreementUrl)
    .then((response) => {
      if (response && response.data) {
        dispatch({
          type: REQUEST_AGREEMENT_CONTENT_SUCCESS,
          payload: response.data,
        });
      }
    })
    .catch((error) => {
      dispatch({
        type: REQUEST_AGREEMENT_CONTENT_FAIL,
        error,
      });
      throw error;
    });
};

export const requestPropsData = (propsUrl) => (dispatch) => {
  dispatch({
    type: REQUEST_PROPS_CONTENT,
  });
  axios
    .get(propsUrl)
    .then((response) => {
      if (response && response.data) {
        dispatch({
          type: REQUEST_PROPS_CONTENT_SUCCESS,
          payload: response.data,
        });
      }
    })
    .catch((error) => {
      dispatch({
        type: REQUEST_PROPS_CONTENT_FAIL,
        error,
      });
      throw error;
    });
};

export const downloadProductOffices = (url, id) => (dispatch, getState) => {
  const state = getState();
  const data = {
    productId: id,
    promotionId: getCatalogProductPromotionId(state)[id],
  };
  // flags to prevent race condition with products update
  const requestStatus = getCatalogProductsRequestStatus(state);
  const officesRequestStatus = getCatalogOfficesRequestStatus(state);
  // prevent multiple downloads for single product
  const productOfficesLength = getCatalogProductOfficesLength(state)[id];
  const productTitle = getCatalogProductTitle(state)[id];
  const previousHoveredId = getCatalogHoveredProductId(state);
  // set currently active productId and productName
  if (!previousHoveredId || id !== previousHoveredId) {
    dispatch(setCatalogHoveredProductId(id));
    dispatch(setCatalogHoveredProductTitle(productTitle));
  }
  // ===
  if (
    productOfficesLength === 0 &&
    officesRequestStatus !== REQUEST_INPROGRESS &&
    requestStatus !== REQUEST_INPROGRESS
  ) {
    dispatch({
      type: REQUEST_CATALOG_PRODUCT_OFFICES,
    });
    const token = getRequestVerificationToken(state);
    axios({
      method: 'POST',
      url,
      data,
      headers: {
        __RequestVerificationToken: token,
        'X-Requested-With': 'XMLHttpRequest',
      },
    })
      .then(({ data: responseData }) => {
        if (responseData.IsSucceeded) {
          dispatch(requestCatalogProductOfficesSuccess(data, responseData.View));
        } else if (!responseData.IsSucceeded) {
          throw responseData.Errors;
        }
      })
      .catch((err) => {
        if (!axios.isCancel(err)) {
          // do not throw error if request was cancelled manually
          dispatch(requestCatalogProductOfficesError(data, { message: err }));
          throw err;
        }
      });
  } else if (productOfficesLength !== 0) {
    const productOffices = getCatalogProductOffices(state)[id];
    dispatch(setCatalogProductOffices(productOffices));
  }
  // ===
  const currentAgreementContent = getCatalogDeliveryAgreementContent(state);
  if (!currentAgreementContent || currentAgreementContent.length === 0) {
    const agreementUrl = getCatalogDeliveryAgreementUrl(state);
    dispatch(requestAgreementData(agreementUrl));
  }
  // ===
  const currentPropsContent = getCatalogDeliveryPropsContent(state);
  if (!currentPropsContent || currentPropsContent.length === 0) {
    const propsUrl = getCatalogDeliveryPropsUrl(state);
    dispatch(requestPropsData(propsUrl));
  }
  // ===
};

export const subscribeOutOfStockProduct = (productId) => (dispatch, getState) => {
  const state = getState();
  const text = getOutOfStockSuccessText(state);
  const emoji = UP_EMOJI;
  const className = INFO_CLASSNAME;

  dispatch({
    type: SUBSCRIBED_PRODUCT,
    className,
    emoji,
    productId,
    text,
  });
};

export const errorSubscribeOutOfStockProduct = (productId) => (dispatch, getState) => {
  const state = getState();
  const text = getOutOfStockErrorText(state);
  const emoji = STATUS_FAIL_EMOJI;
  const className = ERROR_CLASSNAME;
  dispatch({
    type: SUBSCRIBED_PRODUCT,
    className,
    emoji,
    productId,
    text,
  });
};

export const addCatalogItemToBasket =
  ({ id, price, brand, source, forceRedirect, handleSetCartData }) =>
  (dispatch, getState) => {
    const state = getState();
    const isTariff = getProductIsTariffDict(state)[id];
    const isInBasket = getProductInBasketDict(state)[id];
    let fancyNumberData = null;
    if (isTariff) {
      const activeNumber = getActiveFancyNumber(state);
      const dpcId = getProductDpcIdDict(state)[id];
      fancyNumberData = {
        ctn: activeNumber,
        dpc: `-${dpcId}`,
        reserve: null,
      };
    }
    dispatch(
      addProductToBasket({
        isInBasket,
        productId: id,
        forceRedirect,
        fancynumber: fancyNumberData,
        isTariff,
        source,
        price,
        brand,
        handleSetCartData,
      }),
    );
  };

export const onBannerView = (markup) => () => {
  pushEvent(EVENT_CATALOG_VIEW_PROMO_BANNER, markup);
};

export const onBannerClick = (markup) => () => {
  pushEvent(EVENT_CATALOG_CLICK_PROMO_BANNER, markup);
};

export const setFiltersBarChange = (payload) => ({
  type: SET_CATALOG_FILTERS_VALUES_CHANGED,
  payload,
});

export const updateCatalogFilters = (filters) => (dispatch, getState) => {
  const state = getState();
  const lastSettings = getAllFiltersSettings(state);
  const valueFrom = getFilterRangeValueFromRaw(state);
  const valueTo = getFilterRangeValueToRaw(state);
  const used = getFilterRangeUsed(state);
  // selector only on out of stock page when click clear button
  const rewriteFilters = getCatalogForcedRemainsOnly(state);

  // on cases when options count changed during user session
  const emptyOption = {
    isChecked: false,
    isDisabled: false,
    productsCount: 0,
  };
  // ===
  const payload = (filters || []).map((item) => {
    if (rewriteFilters && (item.options || []).length !== 0) {
      return {
        ...item,
        optionsToShowCount: (lastSettings[item.slug] || {}).optionsToShowCount,
      };
    }
    if (item.slug === FILTER_SLUG_PRICE) {
      return {
        ...item,
        valueTo,
        valueFrom,
        used,
      };
    }
    // ===

    const optionsList = {};
    item.options.forEach((option) => {
      optionsList[option.slug] = { ...option };
    });
    // ===
    const options = (lastSettings[item.slug] || item).options.map((option) => {
      const { isChecked, isDisabled, productsCount } = optionsList[option.slug] || emptyOption;
      return {
        ...option,
        isChecked,
        isDisabled,
        productsCount,
      };
    });
    return {
      ...item,
      optionsToShowCount: (lastSettings[item.slug] || {}).optionsToShowCount,
      isOpen: (lastSettings[item.slug] || {}).isOpen || item.isOpen,
      options,
    };
  });

  return dispatch({
    type: SET_FILTER_VALUES,
    payload,
  });
};

export const forceRemainsOnly = (flag) => ({
  type: FORCE_REMAINS_ONLY,
  forcedRemainsOnly: flag || null,
});

export const onNestedCollectionView =
  ({ tabs, id }) =>
  (dispatch) => {
    const index = tabs.findIndex((tab) => tab.id === id);
    const title = tabs[index]?.title;
    dispatch(
      fireEventOnce(`${EVENT_NESTED_COLLECTION_ITEM_VIEW}_${title}`, () =>
        pushEvent(EVENT_NESTED_COLLECTION_ITEM_VIEW, { title, index }),
      ),
    );
  };

export const onNestedCollectionChange =
  ({ url, tabs }) =>
  () => {
    const index = tabs?.findIndex((tab) => tab?.id === url);
    const title = tabs?.[index]?.title;
    pushEvent(EVENT_NESTED_COLLECTION_ITEM_CLICK, { title, index });
    window.location.href = url;
  };

export const onOpenDeviceCatalogModal = (device) => (_, getState) => {
  const filtersDataAnalytics = selectAllFiltersForAnalytics(getState());
  pushEvent(EVENT_FILTER_SELECT, {
    title: filtersDataAnalytics?.title,
    items: filtersDataAnalytics?.totalFoundItems,
    filters: filtersDataAnalytics?.filters,
  });
  pushEvent(EVENT_CATALOG_PRODUCT_CLICK_WITH_BADGES, {
    badges: device?.badges,
    article: device?.article,
  });
  /**
   * По задаче https://servicedesk.veon.com/browse/ES-4880 необходимо включить обратно модалку.
   * Ждем toggle на включение/выключение от .NET
   */
  window.location.href = device?.url;
};

export const onProductCatalogMount = () => (_, getState) => {
  document.body.addEventListener(
    'click',
    (event) => {
      const elementWithLink = event?.composedPath()?.find((elem) => elem?.tagName === 'A');
      // Если переход не в карточку, то делаем пуш значений фильтров
      if (elementWithLink && !elementWithLink?.href?.includes(DETAILS_PART_OF_PATH)) {
        const filtersDataAnalytics = selectAllFiltersForAnalytics(getState());
        pushEvent(EVENT_FILTER_CLEAR, {
          title: filtersDataAnalytics?.title,
          items: filtersDataAnalytics?.totalFoundItems,
          filters: filtersDataAnalytics?.filters,
        });
      }
    },
    false,
  );
};
